import type { SupabaseClient } from '@supabase/supabase-js';
import type { Database } from '@/integrations/supabase/types';

export const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

export type TemplateReferenceResolutionMethod = 'id' | 'slug' | 'snaplink' | null;

export interface TemplateReferenceResolutionResult {
  template: Database['public']['Tables']['templates']['Row'] | null;
  error: string | null;
  requestedReference: string;
  resolvedBy: TemplateReferenceResolutionMethod;
}

async function fetchTemplateById(
  client: SupabaseClient<Database>,
  templateId: string,
  requestedReference: string,
  resolvedBy: Exclude<TemplateReferenceResolutionMethod, null>,
): Promise<TemplateReferenceResolutionResult> {
  const { data, error } = await client
    .from('templates')
    .select('*')
    .eq('id', templateId)
    .maybeSingle();

  if (error) {
    return {
      template: null,
      error: error.message,
      requestedReference,
      resolvedBy: null,
    };
  }

  return {
    template: data,
    error: data ? null : 'Template not found',
    requestedReference,
    resolvedBy: data ? resolvedBy : null,
  };
}

export async function resolveTemplateRecordByReference(
  client: SupabaseClient<Database>,
  templateReference: string,
): Promise<TemplateReferenceResolutionResult> {
  const requestedReference = templateReference.trim();

  if (!requestedReference) {
    return {
      template: null,
      error: 'No template ID provided',
      requestedReference,
      resolvedBy: null,
    };
  }

  if (UUID_RE.test(requestedReference)) {
    return fetchTemplateById(client, requestedReference, requestedReference, 'id');
  }

  const { data: slugTemplate, error: slugError } = await client
    .from('templates')
    .select('*')
    .eq('slug', requestedReference)
    .maybeSingle();

  if (slugError) {
    return {
      template: null,
      error: slugError.message,
      requestedReference,
      resolvedBy: null,
    };
  }

  if (slugTemplate) {
    return {
      template: slugTemplate,
      error: null,
      requestedReference,
      resolvedBy: 'slug',
    };
  }

  const { data: snaplinkRecord, error: snaplinkError } = await client
    .from('snaplinks')
    .select('template_id')
    .eq('slug', requestedReference)
    .maybeSingle();

  if (snaplinkError) {
    return {
      template: null,
      error: snaplinkError.message,
      requestedReference,
      resolvedBy: null,
    };
  }

  const snaplinkTemplateId = typeof snaplinkRecord?.template_id === 'string'
    ? snaplinkRecord.template_id.trim()
    : '';

  if (!snaplinkTemplateId) {
    return {
      template: null,
      error: 'Template not found',
      requestedReference,
      resolvedBy: null,
    };
  }

  return fetchTemplateById(client, snaplinkTemplateId, requestedReference, 'snaplink');
}